import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import {
  DataGridTable,
  GridPillChip,
  ListTableNavLink,
  listTableStickyBodyScrollSx,
  type DataGridTableRow,
  type ListTableNavLinkTarget,
} from "design-components";
import type { ReactNode } from "react";
import type { OperatorListRow } from "../types";

type OperatorsGridRow = OperatorListRow & DataGridTableRow;

const HEADER_CLASSES = [
  "operators-table__th-company-name",
  "operators-table__th-country",
  "operators-table__th-primary-contact",
  "operators-table__th-email",
  "operators-table__th-phone",
  "operators-table__th-status",
  "operators-table__th-locations",
] as const;

export interface OperatorsTableProps {
  rows: OperatorListRow[];
  selectedOperatorIds: string[];
  sortDescending: boolean;
  onSelectedOperatorIdsChange: (ids: string[]) => void;
  onCompanyNameHeaderClick: () => void;
  getCompanyNameLink?: (row: OperatorListRow) => ListTableNavLinkTarget;
  paginationFooter?: ReactNode;
}

export function OperatorsTable(props: OperatorsTableProps) {
  const {
    rows,
    selectedOperatorIds,
    sortDescending,
    onSelectedOperatorIdsChange,
    onCompanyNameHeaderClick,
    getCompanyNameLink,
    paginationFooter,
  } = props;

  const gridRows: OperatorsGridRow[] = rows.map((row) => ({
    ...row,
    primaryCellLabel: row.companyName,
  }));

  return (
    <DataGridTable<OperatorsGridRow>
      className="users-table operators-table operators-table--aviro"
      sx={{ width: "100%", maxWidth: "100%" }}
      tableContainerClassName="users-table__container"
      tableClassName="users-table__table"
      headRowClassName="users-table__head-row"
      selectionHeaderCellClassName="users-table__th-checkbox"
      selectionBodyCellClassName="users-table__cell-checkbox"
      headerDataCellClassName={(columnIndex) => HEADER_CLASSES[columnIndex]}
      rows={gridRows}
      dataColumnCount={7}
      selectionMode="multiple"
      selectedRowIds={selectedOperatorIds}
      onSelectedRowIdsChange={onSelectedOperatorIdsChange}
      trailingEmptyCell={false}
      getRowStateClass={() => "normal"}
      rowHover
      showSelectionColumn
      footer={paginationFooter}
      stickyHeader={Boolean(paginationFooter)}
      tableContainerSx={paginationFooter ? listTableStickyBodyScrollSx : undefined}
      renderHeaderCell={(columnIndex) => {
        if (columnIndex === 0) {
          return (
            <button
              type="button"
              className="users-table__sort-btn"
              onClick={onCompanyNameHeaderClick}
            >
              <Typography className="aviro-grid-header__cell-text" component="span">
                Company name
              </Typography>
              {sortDescending ? (
                <KeyboardArrowDownIcon className="users-table__sort-icon" aria-hidden />
              ) : (
                <KeyboardArrowUpIcon className="users-table__sort-icon" aria-hidden />
              )}
            </button>
          );
        }
        if (columnIndex === 1) return <Typography className="aviro-grid-header__cell-text">Country</Typography>;
        if (columnIndex === 2) return <Typography className="aviro-grid-header__cell-text">Primary contact</Typography>;
        if (columnIndex === 3) return <Typography className="aviro-grid-header__cell-text">Email</Typography>;
        if (columnIndex === 4) return <Typography className="aviro-grid-header__cell-text">Phone</Typography>;
        if (columnIndex === 5) return <Typography className="aviro-grid-header__cell-text">Status</Typography>;
        return <Typography className="aviro-grid-header__cell-text">Location(s)</Typography>;
      }}
      renderBodyCell={(row, columnIndex) => {
        if (columnIndex === 0) {
          const linkTarget = getCompanyNameLink?.(row);
          if (!linkTarget) {
            return (
              <Typography
                component="span"
                className="users-table__customer-name-link users-table__customer-name-text"
              >
                {row.companyName}
              </Typography>
            );
          }

          return (
            <ListTableNavLink target={linkTarget} className="users-table__customer-name-link">
              {row.companyName}
            </ListTableNavLink>
          );
        }
        if (columnIndex === 1) {
          return <Typography className="aviro-grid-table-cell-text">{row.country}</Typography>;
        }
        if (columnIndex === 2) {
          return <Typography className="aviro-grid-table-cell-text">{row.primaryContact}</Typography>;
        }
        if (columnIndex === 3) {
          return (
            <Typography className="aviro-grid-table-cell-text users-table__cell-ellipsis">
              {row.email}
            </Typography>
          );
        }
        if (columnIndex === 4) {
          return <Typography className="aviro-grid-table-cell-text">{row.phone}</Typography>;
        }
        if (columnIndex === 5) {
          return (
            <GridPillChip
              className="users-table__status-pill users-table__status-pill--customer-active operators-table__status-pill"
              pillVariant={row.status === "active" ? "breeze" : "critical"}
              label={row.status === "active" ? "ACTIVE" : "INACTIVE"}
              size="small"
            />
          );
        }
        return <LocationColumnCell row={row} />;
      }}
    />
  );
}

function locationLabels(locations: string): string[] {
  return locations
    .split(",")
    .map((label) => label.trim())
    .filter(Boolean);
}

function LocationColumnCell({ row }: { row: OperatorListRow }) {
  const labels = locationLabels(row.locations);
  const firstLabel = labels[0] ?? "—";
  const overflowLabels = labels.slice(1);
  const overflowCount = overflowLabels.length;

  return (
    <Box className="users-table__location-cell">
      <Typography component="span" className="aviro-grid-table-cell-text users-table__location-line">
        {firstLabel}
      </Typography>
      {overflowCount > 0 ? (
        <Tooltip
          title={overflowLabels.join(", ")}
          arrow
          describeChild
        >
          <span>
            <Chip
              className="users-table__location-overflow"
              size="small"
              label={`+${overflowCount}`}
              aria-label={`${overflowCount} more locations: ${overflowLabels.join(", ")}`}
              onClick={(event) => event.stopPropagation()}
            />
          </span>
        </Tooltip>
      ) : null}
    </Box>
  );
}